import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Helmet } from 'react-helmet-async';
import { Database, HardDrive, FileText, Users, LogOut, Shield, ShieldCheck, ShieldOff, Upload, Trash2, Download, Search, Calendar, Edit, Eye, EyeOff, Plus, Star, MessageSquare, Image, RefreshCw } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { UserManagementTab } from '@/components/UserManagementTab';
import { ResourceManagementDialog } from '@/components/ResourceManagementDialog';
import { DeleteResourceDialog } from '@/components/DeleteResourceDialog';
import { TestimonialManagementDialog } from '@/components/TestimonialManagementDialog';
import { DeleteTestimonialDialog } from '@/components/DeleteTestimonialDialog';
import { SearchAnalytics } from '@/components/SearchAnalytics';
import FooterLogoManagement from '@/components/FooterLogoManagement';
import ContactSettingsManagement from '@/components/ContactSettingsManagement';
import { BlogManagement } from '@/components/BlogManagement';
import { LeadsManagement } from '@/components/LeadsManagement';
import { LeadDetailsDialog } from '@/components/LeadDetailsDialog';
import { DataRightsManagement } from '@/components/DataRightsManagement';
import { BackupManager } from '@/components/BackupManager';
import { ServiceManagementDialog } from '@/components/ServiceManagementDialog';
import { DeleteServiceDialog } from '@/components/DeleteServiceDialog';
import { CertificateWidgetManagement } from '@/components/CertificateWidgetManagement';
import { TeamMemberManagement } from '@/components/TeamMemberManagement';
import { SiteLogoManagement } from '@/components/SiteLogoManagement';
import { getIconComponent } from '@/utils/iconMapper';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';
import { Switch } from '@/components/ui/switch';

const Admin = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [resources, setResources] = useState<any[]>([]);
  const [downloads, setDownloads] = useState<any[]>([]);
  const [testimonials, setTestimonials] = useState<any[]>([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isManagementDialogOpen, setIsManagementDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedResource, setSelectedResource] = useState<any>(null);
  const [isTestimonialDialogOpen, setIsTestimonialDialogOpen] = useState(false);
  const [isDeleteTestimonialDialogOpen, setIsDeleteTestimonialDialogOpen] = useState(false);
  const [selectedTestimonial, setSelectedTestimonial] = useState<any>(null);
  const [users, setUsers] = useState<any[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [leads, setLeads] = useState<any[]>([]);
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [services, setServices] = useState<any[]>([]);
  const [isServiceDialogOpen, setIsServiceDialogOpen] = useState(false);
  const [isDeleteServiceDialogOpen, setIsDeleteServiceDialogOpen] = useState(false);
  const [selectedService, setSelectedService] = useState<any>(null);
  const [selectedLead, setSelectedLead] = useState<any>(null);
  const [isLeadDetailsOpen, setIsLeadDetailsOpen] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(true);

  useEffect(() => {
    // Only load data when user is authenticated
    if (user) {
      setIsAuthenticating(false);
      loadResources();
      loadDownloads();
      loadTestimonials();
      loadUsers();
      loadLeads();
      loadServices();
    } else {
      // Check if we're still waiting for auth or user is not logged in
      const checkAuth = async () => {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          setIsAuthenticating(false);
        }
      };
      checkAuth();
    }
  }, [user]);

  const loadResources = async () => {
    try {
      const { data, error } = await supabase
        .from('resources')
        .select('*')
        .order('display_order', { ascending: true });
      
      if (error) throw error;
      setResources(data || []);
    } catch (error: any) {
      toast({
        title: 'Error loading resources',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const loadDownloads = async () => {
    try {
      const { data, error } = await supabase
        .from('resource_downloads')
        .select('*')
        .order('downloaded_at', { ascending: false });
      
      if (error) throw error;
      setDownloads(data || []);
    } catch (error: any) {
      toast({
        title: 'Error loading downloads',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const handleAddResource = () => {
    setSelectedResource(null);
    setIsManagementDialogOpen(true);
  };

  const handleEditResource = (resource: any) => {
    setSelectedResource(resource);
    setIsManagementDialogOpen(true);
  };

  const handleDeleteClick = (resource: any) => {
    setSelectedResource(resource);
    setIsDeleteDialogOpen(true);
  };

  const handleTogglePublished = async (resourceId: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('resources')
        .update({ is_published: !currentStatus })
        .eq('id', resourceId);

      if (error) throw error;

      toast({
        title: 'Success',
        description: `Resource ${!currentStatus ? 'published' : 'unpublished'} successfully`,
      });
      loadResources();
    } catch (error: any) {
      toast({
        title: 'Update failed',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const loadTestimonials = async () => {
    try {
      const { data, error } = await supabase
        .from('testimonials')
        .select('*')
        .order('display_order', { ascending: true });
      
      if (error) throw error;
      setTestimonials(data || []);
    } catch (error: any) {
      toast({
        title: 'Error loading testimonials',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const handleAddTestimonial = () => {
    setSelectedTestimonial(null);
    setIsTestimonialDialogOpen(true);
  };

  const handleEditTestimonial = (testimonial: any) => {
    setSelectedTestimonial(testimonial);
    setIsTestimonialDialogOpen(true);
  };

  const handleDeleteTestimonialClick = (testimonial: any) => {
    setSelectedTestimonial(testimonial);
    setIsDeleteTestimonialDialogOpen(true);
  };

  const handleDeleteTestimonialConfirm = async () => {
    if (!selectedTestimonial) return;

    try {
      const { error } = await supabase
        .from('testimonials')
        .delete()
        .eq('id', selectedTestimonial.id);

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Testimonial deleted successfully',
      });
      loadTestimonials();
      setIsDeleteTestimonialDialogOpen(false);
    } catch (error: any) {
      toast({
        title: 'Delete failed',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const handleToggleTestimonialPublished = async (testimonialId: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('testimonials')
        .update({ is_published: !currentStatus })
        .eq('id', testimonialId);

      if (error) throw error;

      toast({
        title: 'Success',
        description: `Testimonial ${!currentStatus ? 'published' : 'unpublished'} successfully`,
      });
      loadTestimonials();
    } catch (error: any) {
      toast({
        title: 'Update failed',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const loadUsers = async () => {
    setLoadingUsers(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        toast({
          title: 'Authentication required',
          description: 'Please log in to view users',
          variant: 'destructive',
        });
        return;
      }

      const { data, error } = await supabase.functions.invoke('get-all-users', {
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });

      if (error) {
        throw error;
      }

      setUsers(data.users || []);
    } catch (error: any) {
      console.error('Error loading users:', error);
      toast({
        title: 'Error loading users',
        description: error.message || 'Failed to load user list',
        variant: 'destructive',
      });
    } finally {
      setLoadingUsers(false);
    }
  };

  const loadServices = async () => {
    try {
      const { data, error } = await supabase
        .from('services')
        .select('*')
        .order('display_order', { ascending: true });
      
      if (error) throw error;
      setServices(data || []);
    } catch (error: any) {
      toast({
        title: 'Error loading services',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const handleAddService = () => {
    setSelectedService(null);
    setIsServiceDialogOpen(true);
  };

  const handleEditService = (service: any) => {
    setSelectedService(service);
    setIsServiceDialogOpen(true);
  };

  const handleDeleteServiceClick = (service: any) => {
    setSelectedService(service);
    setIsDeleteServiceDialogOpen(true);
  };

  const handleToggleServiceActive = async (serviceId: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('services')
        .update({ is_active: !currentStatus })
        .eq('id', serviceId);

      if (error) throw error;

      toast({
        title: 'Success',
        description: `Service ${!currentStatus ? 'activated' : 'deactivated'} successfully`,
      });
      loadServices();
    } catch (error: any) {
      toast({
        title: 'Update failed',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const loadLeads = async () => {
    setLoadingLeads(true);
    
    try {
      // Fetch all data sources in parallel
      const [leadsResult, downloadsResult, assessmentsResult] = await Promise.all([
        supabase.from('leads').select('*'),
        supabase.from('resource_downloads').select('*'),
        supabase.from('assessment_results').select('*')
      ]);

      if (leadsResult.error) throw leadsResult.error;
      if (downloadsResult.error) throw downloadsResult.error;
      if (assessmentsResult.error) throw assessmentsResult.error;

      const allLeads = leadsResult.data || [];
      const allDownloads = downloadsResult.data || [];
      const allAssessments = assessmentsResult.data || [];

      // Group all activities by email
      const emailMap = new Map();

      // Process leads from leads table
      allLeads.forEach(lead => {
        if (!emailMap.has(lead.email)) {
          emailMap.set(lead.email, {
            email: lead.email,
            name: lead.name,
            phone: lead.phone,
            company: lead.company,
            lead_source: lead.lead_source,
            lead_type: lead.lead_type,
            status: lead.status,
            notes: lead.notes,
            activities: [],
            contactForms: 0,
            downloads: 0,
            assessments: 0,
            downloadDetails: []
          });
        }
        const contact = emailMap.get(lead.email);
        contact.activities.push({ type: 'contact_form', date: lead.created_at });
        contact.contactForms++;
        // Update contact info with non-null values
        if (!contact.name && lead.name) contact.name = lead.name;
        if (!contact.phone && lead.phone) contact.phone = lead.phone;
        if (!contact.company && lead.company) contact.company = lead.company;
      });

      // Process resource downloads
      allDownloads.forEach(download => {
        if (!emailMap.has(download.email)) {
          emailMap.set(download.email, {
            email: download.email,
            name: null,
            phone: null,
            company: null,
            lead_source: 'resource_download',
            lead_type: 'resource',
            status: 'new',
            notes: null,
            activities: [],
            contactForms: 0,
            downloads: 0,
            assessments: 0,
            downloadDetails: []
          });
        }
        const contact = emailMap.get(download.email);
        contact.activities.push({ type: 'download', date: download.downloaded_at });
        contact.downloads++;
        contact.downloadDetails.push({
          resourceName: download.resource_name,
          resourceType: download.resource_type,
          downloadedAt: download.downloaded_at
        });
      });

      // Process assessment results
      allAssessments.forEach(assessment => {
        if (!emailMap.has(assessment.email)) {
          emailMap.set(assessment.email, {
            email: assessment.email,
            name: assessment.name,
            phone: null,
            company: null,
            lead_source: 'assessment',
            lead_type: 'assessment',
            status: assessment.consultation_requested ? 'qualified' : 'new',
            notes: null,
            activities: [],
            contactForms: 0,
            downloads: 0,
            assessments: 0,
            downloadDetails: []
          });
        }
        const contact = emailMap.get(assessment.email);
        contact.activities.push({ type: 'assessment', date: assessment.completed_at });
        contact.assessments++;
        if (!contact.name && assessment.name) contact.name = assessment.name;
        if (assessment.consultation_requested) contact.status = 'qualified';
      });

      // Convert map to aggregated leads array
      const aggregatedLeads = Array.from(emailMap.values()).map(contact => {
        // Sort activities by date to find first and latest
        const sortedActivities = contact.activities.sort((a, b) => 
          new Date(b.date).getTime() - new Date(a.date).getTime()
        );

        return {
          id: contact.email, // Use email as unique ID
          email: contact.email,
          name: contact.name,
          phone: contact.phone,
          company: contact.company,
          lead_source: contact.lead_source,
          lead_type: contact.lead_type,
          status: contact.status,
          notes: contact.notes,
          created_at: sortedActivities[sortedActivities.length - 1]?.date || new Date().toISOString(),
          updated_at: sortedActivities[0]?.date || new Date().toISOString(),
          totalActivities: contact.activities.length,
          activityBreakdown: {
            downloads: contact.downloads,
            assessments: contact.assessments,
            contactForms: contact.contactForms
          },
          downloadDetails: contact.downloadDetails || []
        };
      });

      // Sort by latest activity (most recent first)
      aggregatedLeads.sort((a, b) => 
        new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
      );

      setLeads(aggregatedLeads);
    } catch (error: any) {
      toast({
        title: 'Error loading leads',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setLoadingLeads(false);
    }
  };

  const filteredDownloads = downloads.filter(
    (download) =>
      download.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      download.resource_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const downloadStats = {
    total: downloads.length,
    uniqueEmails: new Set(downloads.map((d) => d.email)).size,
    last7Days: downloads.filter(
      (d) => new Date(d.downloaded_at) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
    ).length,
  };

  const AdminLoadingSkeleton = () => (
    <div className="container mx-auto px-4 max-w-6xl">
      <div className="flex justify-between items-center mb-8">
        <div className="space-y-2">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-4 w-48" />
        </div>
        <Skeleton className="h-10 w-24" />
      </div>

      <div className="space-y-6">
        <Skeleton className="h-10 w-full" />
        
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="space-y-2">
                <Skeleton className="h-6 w-48" />
                <Skeleton className="h-4 w-64" />
              </div>
              <Skeleton className="h-10 w-32" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );

  return (
    <>
      <Helmet>
        <title>Admin Dashboard - 4FOX Security</title>
        <meta name="robots" content="noindex, nofollow" />
        <meta name="googlebot" content="noindex, nofollow" />
      </Helmet>
      <div className="min-h-screen flex flex-col">
        <Navbar />
        
        <main className="flex-1 pt-24 pb-16 bg-secondary/30">
          {isAuthenticating ? (
            <AdminLoadingSkeleton />
          ) : (
            <div className="container mx-auto px-4 max-w-6xl">
            <div className="flex justify-between items-center mb-8">
              <div>
                <h1 className="text-4xl font-bold mb-2">Admin Dashboard</h1>
                <p className="text-muted-foreground">
                  Welcome back, {user?.email}
                </p>
              </div>
              <Button 
                variant="outline" 
                onClick={handleSignOut}
                className="gap-2"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </Button>
            </div>

            <Tabs defaultValue="leads" className="space-y-6">
              <TabsList className="flex flex-wrap gap-1 h-auto w-full">
                <TabsTrigger value="leads">Leads</TabsTrigger>
                <TabsTrigger value="users">Users</TabsTrigger>
                <TabsTrigger value="blog">Blog</TabsTrigger>
                <TabsTrigger value="search">Search</TabsTrigger>
                <TabsTrigger value="testimonials">Testimonials</TabsTrigger>
                <TabsTrigger value="resources">Resources</TabsTrigger>
                <TabsTrigger value="services">Services</TabsTrigger>
                <TabsTrigger value="team">Team</TabsTrigger>
                <TabsTrigger value="data-rights">Data Rights</TabsTrigger>
                <TabsTrigger value="contact">Contact</TabsTrigger>
                <TabsTrigger value="logos">Logos</TabsTrigger>
                <TabsTrigger value="certificates">Certificates</TabsTrigger>
                <TabsTrigger value="backup">Backup</TabsTrigger>
              </TabsList>

              <TabsContent value="leads" className="space-y-4">
                <LeadsManagement 
                  leads={leads} 
                  loading={loadingLeads} 
                  onRefresh={loadLeads}
                  onLeadClick={(lead) => {
                    setSelectedLead(lead);
                    setIsLeadDetailsOpen(true);
                  }}
                />
              </TabsContent>

              <TabsContent value="blog" className="space-y-4">
                <BlogManagement />
              </TabsContent>

              <TabsContent value="search" className="space-y-4">
                <SearchAnalytics />
              </TabsContent>

              <TabsContent value="resources" className="space-y-4">
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          <HardDrive className="w-5 h-5" />
                          Resource Management
                        </CardTitle>
                        <CardDescription>
                          Manage downloadable resources with metadata
                        </CardDescription>
                      </div>
                      <Button onClick={handleAddResource}>
                        <Plus className="w-4 h-4 mr-2" />
                        Add Resource
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="border rounded-lg">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Title</TableHead>
                            <TableHead>Category</TableHead>
                            <TableHead>Type</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Downloads</TableHead>
                            <TableHead>Created</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {resources.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={7} className="text-center text-muted-foreground">
                                No resources created yet. Click "Add Resource" to get started.
                              </TableCell>
                            </TableRow>
                          ) : (
                            resources.map((resource) => (
                              <TableRow key={resource.id}>
                                <TableCell className="font-medium max-w-xs">
                                  <div className="flex items-center gap-2">
                                    {(() => {
                                      const Icon = getIconComponent(resource.icon_name);
                                      return <Icon className="w-4 h-4 text-accent" />;
                                    })()}
                                    <span className="truncate">{resource.title}</span>
                                  </div>
                                </TableCell>
                                <TableCell>{resource.category}</TableCell>
                                <TableCell>{resource.resource_type}</TableCell>
                                <TableCell>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleTogglePublished(resource.id, resource.is_published)}
                                  >
                                    {resource.is_published ? (
                                      <Eye className="w-4 h-4 text-success" />
                                    ) : (
                                      <EyeOff className="w-4 h-4 text-muted-foreground" />
                                    )}
                                  </Button>
                                </TableCell>
                                <TableCell>{resource.download_count}</TableCell>
                                <TableCell>
                                  {format(new Date(resource.created_at), 'MMM d, yyyy')}
                                </TableCell>
                                <TableCell className="text-right">
                                  <div className="flex justify-end gap-1">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleEditResource(resource)}
                                    >
                                      <Edit className="w-4 h-4" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleDeleteClick(resource)}
                                    >
                                      <Trash2 className="w-4 h-4 text-destructive" />
                                    </Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="testimonials" className="space-y-4">
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          <MessageSquare className="w-5 h-5" />
                          Testimonial Management
                        </CardTitle>
                        <CardDescription>
                          Manage client testimonials and reviews
                        </CardDescription>
                      </div>
                      <Button onClick={handleAddTestimonial}>
                        <Plus className="w-4 h-4 mr-2" />
                        Add Testimonial
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="border rounded-lg">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Client</TableHead>
                            <TableHead>Company</TableHead>
                            <TableHead>Rating</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Featured</TableHead>
                            <TableHead>Created</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {testimonials.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={7} className="text-center text-muted-foreground">
                                No testimonials created yet. Click "Add Testimonial" to get started.
                              </TableCell>
                            </TableRow>
                          ) : (
                            testimonials.map((testimonial) => (
                              <TableRow key={testimonial.id}>
                                <TableCell className="font-medium">
                                  {testimonial.client_name}
                                </TableCell>
                                <TableCell>{testimonial.client_company}</TableCell>
                                <TableCell>
                                  <div className="flex gap-0.5">
                                    {[1, 2, 3, 4, 5].map((star) => (
                                      <Star
                                        key={star}
                                        className={`h-4 w-4 ${
                                          star <= testimonial.rating
                                            ? "fill-yellow-400 text-yellow-400"
                                            : "text-muted-foreground/30"
                                        }`}
                                      />
                                    ))}
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleToggleTestimonialPublished(testimonial.id, testimonial.is_published)}
                                  >
                                    {testimonial.is_published ? (
                                      <Eye className="w-4 h-4 text-success" />
                                    ) : (
                                      <EyeOff className="w-4 h-4 text-muted-foreground" />
                                    )}
                                  </Button>
                                </TableCell>
                                <TableCell>
                                  {testimonial.featured && (
                                    <Badge variant="secondary">Featured</Badge>
                                  )}
                                </TableCell>
                                <TableCell>
                                  {format(new Date(testimonial.created_at), 'MMM d, yyyy')}
                                </TableCell>
                                <TableCell className="text-right">
                                  <div className="flex justify-end gap-1">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleEditTestimonial(testimonial)}
                                    >
                                      <Edit className="w-4 h-4" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleDeleteTestimonialClick(testimonial)}
                                    >
                                      <Trash2 className="w-4 h-4 text-destructive" />
                                    </Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="users" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Users className="w-5 h-5" />
                      User Management
                    </CardTitle>
                    <CardDescription>
                      View and manage registered users
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {loadingUsers ? (
                      <div className="flex items-center justify-center py-8">
                        <RefreshCw className="w-6 h-6 animate-spin text-primary" />
                      </div>
                    ) : (
                      <UserManagementTab users={users} onRefresh={loadUsers} />
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="services" className="space-y-4">
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          <Shield className="w-5 h-5" />
                          Service Management
                        </CardTitle>
                        <CardDescription>
                          Manage service offerings and visibility
                        </CardDescription>
                      </div>
                      <Button onClick={handleAddService}>
                        <Plus className="w-4 h-4 mr-2" />
                        Add Service
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="border rounded-lg">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Title</TableHead>
                            <TableHead>URL</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Order</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {services.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={5} className="text-center text-muted-foreground">
                                No services created yet. Click "Add Service" to get started.
                              </TableCell>
                            </TableRow>
                          ) : (
                            services.map((service) => (
                              <TableRow key={service.id}>
                                <TableCell className="font-medium max-w-xs">
                                  <div className="flex items-center gap-2">
                                    {(() => {
                                      const Icon = getIconComponent(service.icon_name);
                                      return <Icon className="w-4 h-4 text-accent" />;
                                    })()}
                                    <span className="truncate">{service.title}</span>
                                  </div>
                                </TableCell>
                                <TableCell className="text-sm text-muted-foreground">
                                  {service.url}
                                </TableCell>
                                <TableCell>
                                  <div className="flex items-center gap-2">
                                    <Switch
                                      checked={service.is_active}
                                      onCheckedChange={() => handleToggleServiceActive(service.id, service.is_active)}
                                    />
                                    <Badge variant={service.is_active ? "default" : "secondary"}>
                                      {service.is_active ? "Active" : "Inactive"}
                                    </Badge>
                                  </div>
                                </TableCell>
                                <TableCell>{service.display_order}</TableCell>
                                <TableCell className="text-right">
                                  <div className="flex justify-end gap-1">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleEditService(service)}
                                    >
                                      <Edit className="w-4 h-4" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleDeleteServiceClick(service)}
                                    >
                                      <Trash2 className="w-4 h-4 text-destructive" />
                                    </Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="logos" className="space-y-6">
                <SiteLogoManagement />
                <Card>
                  <CardHeader>
                    <CardTitle>Footer Logos</CardTitle>
                    <CardDescription>Manage certification and partner logos displayed in the footer.</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <FooterLogoManagement />
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="certificates" className="space-y-4">
                <Card>
                  <CardContent className="pt-6">
                    <CertificateWidgetManagement />
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="team" className="space-y-4">
                <TeamMemberManagement />
              </TabsContent>

              <TabsContent value="contact" className="space-y-4">
                <ContactSettingsManagement />
              </TabsContent>

              <TabsContent value="data-rights" className="space-y-4">
                <DataRightsManagement />
              </TabsContent>

              <TabsContent value="backup" className="space-y-4">
                <BackupManager />
              </TabsContent>
            </Tabs>
          </div>
          )}
        </main>

        <Footer />
      </div>

      <ResourceManagementDialog
        open={isManagementDialogOpen}
        onOpenChange={setIsManagementDialogOpen}
        onSuccess={loadResources}
        existingResource={selectedResource}
      />

      <DeleteResourceDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        resource={selectedResource}
        onSuccess={loadResources}
      />

      <TestimonialManagementDialog
        open={isTestimonialDialogOpen}
        onOpenChange={setIsTestimonialDialogOpen}
        testimonial={selectedTestimonial}
        onSuccess={loadTestimonials}
      />

      <DeleteTestimonialDialog
        open={isDeleteTestimonialDialogOpen}
        onOpenChange={setIsDeleteTestimonialDialogOpen}
        testimonialName={selectedTestimonial?.client_name || ''}
        testimonialCompany={selectedTestimonial?.client_company || ''}
        onConfirm={handleDeleteTestimonialConfirm}
      />

      <ServiceManagementDialog
        open={isServiceDialogOpen}
        onOpenChange={setIsServiceDialogOpen}
        service={selectedService}
        onSuccess={loadServices}
      />

      <DeleteServiceDialog
        open={isDeleteServiceDialogOpen}
        onOpenChange={setIsDeleteServiceDialogOpen}
        service={selectedService}
        onSuccess={loadServices}
      />

      <LeadDetailsDialog
        lead={selectedLead}
        open={isLeadDetailsOpen}
        onOpenChange={setIsLeadDetailsOpen}
        onUpdate={loadLeads}
      />
    </>
  );
};

export default Admin;
